/**
 * StaffLayout — authenticated operator console shell.
 *
 * Ported (and trimmed) from `screens/src/layouts/AppLayout.jsx`. Renders the
 * collapsible sidebar + topbar + outlet. Phase 2a only wires the live links
 * Phase 2 has shipped (Dashboard, Mail Register/Drafts, Approvals); the
 * remaining sections are surfaced as disabled-with-tooltip entries so the
 * information architecture stays visible to stakeholders.
 *
 * The layout deliberately does NOT depend on screens-only modules
 * (data/navigation, GlobalSearch, HardwareStatusStrip, NotificationBell). Each
 * of those will land as its own slice in later phases — for now the topbar
 * shows the search trigger + notification bell as visual placeholders.
 *
 * Replaces the Phase-0 `apps/web-staff/src/layouts/AppLayout.tsx`. Existing
 * mail-register routes (/mail/register, /mail/drafts) now render through this
 * layout — their page-level markup is untouched.
 */
import { type JSX, useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { ApiError } from '../lib/api/client';
import { lookupCustomers } from '../lib/api/customers';
import { lookupMailItemByTracking } from '../lib/api/mail-items';
import type { CustomerResponse } from '../lib/api/types';
import {
  Bell,
  Building2,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  CreditCard,
  FileText,
  Gauge,
  HeadphonesIcon,
  LayoutDashboard,
  ListChecks,
  LogOut,
  type LucideIcon,
  Mail as MailIcon,
  Map,
  Menu,
  Network,
  Package,
  Printer,
  Search,
  Settings,
  ShieldCheck,
  Store,
  Truck,
  User as UserIcon,
  Workflow,
} from 'lucide-react';

import { useAuth } from '../lib/auth/useAuth';
import { decodeStaffClaims } from '../lib/auth/jwt';

interface NavLeaf {
  readonly label: string;
  readonly to: string;
  readonly enabled: true;
  readonly badge?: number;
}
interface NavLeafDisabled {
  readonly label: string;
  readonly enabled: false;
  readonly tooltip: string;
}
type NavLeafItem = NavLeaf | NavLeafDisabled;

interface NavSection {
  readonly key: string;
  readonly label: string;
  readonly icon: LucideIcon;
  readonly to?: string;
  readonly enabled: boolean;
  readonly tooltip?: string;
  readonly children?: readonly NavLeafItem[];
  readonly badge?: number;
}

/* Sidebar navigation. Live entries match the routes wired in Phase 2a; the
   rest carry a "Phase X coming" tooltip so the IA stays visible. The Mail
   parent expands to surface the two live mail screens plus 20 disabled
   placeholders that Phase 2b will activate. */
const NAV_SECTIONS: readonly NavSection[] = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    to: '/',
    enabled: true,
  },
  {
    key: 'mail',
    label: 'Mail',
    icon: MailIcon,
    enabled: true,
    children: [
      { label: 'Mail Dashboard',            to: '/mail',                       enabled: true },
      { label: 'Register Mail',             to: '/mail/register',              enabled: true },
      { label: 'Bulk Registration',         to: '/mail/register/bulk',         enabled: true },
      { label: 'My Drafts',                 to: '/mail/drafts',                enabled: true },
      { label: 'All Items',                 to: '/mail/items',                 enabled: true },
      { label: 'Track Mail',                to: '/mail/track',                 enabled: true },
      { label: 'Sorting Workflow',          to: '/mail/sorting',               enabled: true },
      { label: 'Cross-Dock Manifests',      to: '/mail/manifests',             enabled: true },
      { label: 'Return to Sender',          to: '/mail/returns',               enabled: true },
      { label: 'Damaged Item Handling',     to: '/mail/damages',               enabled: true },
      { label: 'Weight Discrepancy',        to: '/mail/weight-discrepancy',    enabled: true },
      { label: 'Error Resolution',          to: '/mail/errors',                enabled: true },
      { label: 'Supervisor Approvals',      to: '/mail/supervisor',            enabled: true },
      { label: 'Customer Profile Mgmt',     to: '/mail/customer-profile',      enabled: true },
      { label: 'PO Box Management',         to: '/mail/pobox',                 enabled: true },
      { label: 'Cash Reconciliation',       to: '/mail/cash-recon',            enabled: true },
      { label: 'Daily Transaction Summary', to: '/mail/transactions/daily',    enabled: true },
      { label: 'Counter Payment',           to: '/mail/payments/counter',      enabled: true },
      { label: 'Shift Management',          to: '/mail/shift',                 enabled: true },
      { label: 'Label Generation',          to: '/mail/labels',                enabled: true },
      { label: 'Receipt Preview',           to: '/mail/receipts',              enabled: true },
      { label: 'NIN Verify',                to: '/mail/nin-verify',            enabled: true },
    ],
  },
  {
    key: 'approvals',
    label: 'Approvals',
    icon: ListChecks,
    to: '/approvals',
    enabled: true,
  },
  {
    key: 'delivery',
    label: 'Delivery',
    icon: Truck,
    enabled: true,
    children: [
      { label: 'Dashboard',     to: '/delivery',             enabled: true },
      { label: 'Routes',        to: '/delivery/routes',      enabled: true },
      { label: 'Assignment',    to: '/delivery/assignment',  enabled: true },
      { label: 'Drivers',       to: '/delivery/drivers',     enabled: true },
      { label: 'POD Review',    to: '/delivery/pod',         enabled: true },
      { label: 'Exceptions',    to: '/delivery/exceptions',  enabled: true },
      { label: 'Disputes',      to: '/delivery/disputes',    enabled: true },
      { label: 'Performance',   to: '/delivery/performance', enabled: true },
    ],
  },
  {
    key: 'fleet',
    label: 'Fleet',
    icon: Package,
    enabled: true,
    children: [
      { label: 'Dashboard',         to: '/fleet',          enabled: true },
      { label: 'Register Vehicle',  to: '/fleet/register', enabled: true },
    ],
  },
  {
    key: 'customer-service',
    label: 'Customer Service',
    icon: HeadphonesIcon,
    enabled: true,
    children: [
      { label: 'Dashboard',          to: '/customer-service',              enabled: true },
      { label: 'Customer Lookup',    to: '/customer-service/lookup',       enabled: true },
      { label: 'Complaints',         to: '/customer-service/complaints',   enabled: true },
      { label: 'Segments',           to: '/customer-service/segments',     enabled: true },
      { label: 'Escalations',        to: '/customer-service/escalations',  enabled: true },
      { label: 'Feedback',           to: '/customer-service/feedback',     enabled: true },
      { label: 'Public Tracking',    to: '/customer-service/track',        enabled: true },
    ],
  },
  {
    key: 'admin-customers',
    label: 'Customers',
    icon: UserIcon,
    enabled: true,
    children: [
      { label: 'Browse',              to: '/admin/customers',           enabled: true },
      { label: 'Reports / Dashboard', to: '/admin/customers/dashboard', enabled: true },
    ],
  },
  {
    key: 'franchise',
    label: 'Franchise',
    icon: Store,
    enabled: true,
    children: [
      { label: 'Dashboard',          to: '/franchise',                enabled: true },
      { label: 'Applications',       to: '/franchise/applications',   enabled: true },
      { label: 'Hierarchy',          to: '/franchise/hierarchy',      enabled: true },
      { label: 'Agreements',         to: '/franchise/agreements',     enabled: true },
      { label: 'Tier definitions',   to: '/franchise/tiers',          enabled: true },
      { label: 'Service catalogue',  to: '/franchise/services',       enabled: true },
      { label: 'Provisioning',       to: '/franchise/provisioning',   enabled: true },
      { label: 'Devices',            to: '/franchise/devices',        enabled: true },
      { label: 'Certification',       to: '/franchise/certification',  enabled: true },
      { label: 'Settlement tracking', to: '/franchise/settlements',     enabled: true },
      { label: 'Remittance',          to: '/franchise/remittance',      enabled: true },
      { label: 'Revenue share',       to: '/franchise/revenue-share',   enabled: true },
      { label: 'Incentives',          to: '/franchise/incentives',      enabled: true },
      { label: 'Performance',         to: '/franchise/performance',     enabled: true },
      { label: 'Reconciliation',      to: '/franchise/reconciliation',  enabled: true },
      { label: 'Suspensions',         to: '/franchise/suspension',      enabled: true },
      { label: 'Inventory',           to: '/franchise/inventory',       enabled: true },
      { label: 'Broadcasts',          to: '/franchise/broadcasts',      enabled: true },
      { label: 'Training dashboard',  to: '/franchise/training',        enabled: true },
      { label: 'Transaction log',     to: '/franchise/transactions',    enabled: true },
      { label: 'Discrepancy',         to: '/franchise/discrepancies',   enabled: true },
      { label: 'National map',        to: '/franchise/national-map',    enabled: true },
    ],
  },
  { key: 'finance',          label: 'Finance',          icon: CreditCard,      enabled: false, tooltip: 'Phase 5 coming' },
  {
    key: 'financial-services',
    label: 'Financial Services',
    icon: CreditCard,
    enabled: true,
    children: [
      { label: 'Dashboard',      to: '/financial-services',                enabled: true },
      { label: 'POS',            to: '/financial-services/pos',            enabled: true },
      { label: 'Money transfers', to: '/financial-services/transfers',     enabled: true },
      { label: 'Bill payments',   to: '/financial-services/bill-payments', enabled: true },
      { label: 'Fraud alerts',    to: '/financial-services/fraud',         enabled: true },
    ],
  },
  {
    key: 'kyc',
    label: 'KYC',
    icon: ShieldCheck,
    enabled: true,
    children: [
      { label: 'Dashboard',  to: '/kyc',            enabled: true },
      { label: 'Verify',     to: '/kyc/verify',     enabled: true },
      { label: 'Analytics',  to: '/kyc/analytics',  enabled: true },
      { label: 'Compliance', to: '/kyc/compliance', enabled: true },
      { label: 'SAR',        to: '/kyc/sar',        enabled: true },
      { label: 'Watchlist',  to: '/kyc/watchlist',  enabled: true },
    ],
  },
  {
    key: 'government',
    label: 'Government',
    icon: Building2,
    enabled: true,
    children: [
      { label: 'Dashboard',      to: '/government',                 enabled: true },
      { label: 'Agencies',       to: '/government/agencies',        enabled: true },
      { label: 'Intake',         to: '/government/intake',          enabled: true },
      { label: 'Reports',        to: '/government/reports',         enabled: true },
      { label: 'Remittance',     to: '/government/remittance',      enabled: true },
      { label: 'Citizen Lookup', to: '/government/citizen-lookup',  enabled: true },
    ],
  },
  {
    key: 'service-desk',
    label: 'Service Desk',
    icon: ClipboardList,
    enabled: true,
    children: [
      { label: 'Dashboard',      to: '/service-desk',               enabled: true },
      { label: 'Applications',   to: '/service-desk/applications',  enabled: true },
      { label: 'Forms',          to: '/service-desk/forms',         enabled: true },
      { label: 'Notifications',  to: '/service-desk/notifications', enabled: true },
      { label: 'Data Export',    to: '/service-desk/data-export',   enabled: true },
      { label: 'Help Center',    to: '/service-desk/help-center',   enabled: true },
    ],
  },
  {
    key: 'printing',
    label: 'Printing',
    icon: Printer,
    enabled: true,
    children: [
      { label: 'Dashboard', to: '/printing',           enabled: true },
      { label: 'Printers',  to: '/printing/printers',  enabled: true },
      { label: 'Templates', to: '/printing/templates', enabled: true },
    ],
  },
  {
    key: 'reporting',
    label: 'Reporting',
    icon: Gauge,
    enabled: true,
    children: [
      { label: 'Dashboard',          to: '/reporting',                       enabled: true },
      { label: 'Dashboard builder',  to: '/reporting/dashboards/builder',    enabled: true },
      { label: 'Dashboard sharing',  to: '/reporting/dashboards/sharing',    enabled: true },
      { label: 'Report builder',     to: '/reporting/reports/builder',       enabled: true },
      { label: 'Scheduled',          to: '/reporting/scheduled',             enabled: true },
      { label: 'Access audit',       to: '/reporting/access-audit',          enabled: true },
      { label: 'Ad-hoc query',       to: '/reporting/adhoc',                 enabled: true },
      { label: 'KPI config',         to: '/reporting/kpi-config',            enabled: true },
      { label: 'Alerts',             to: '/reporting/alerts',                enabled: true },
      { label: 'Management KPIs',    to: '/reporting/management-kpis',       enabled: true },
      { label: 'Benchmarks',         to: '/reporting/benchmarks',            enabled: true },
      { label: 'Data mining',        to: '/reporting/data-mining',           enabled: true },
      { label: 'Role-scoped',        to: '/reporting/role-scoped',           enabled: true },
      { label: 'Forecasting',        to: '/reporting/forecasting',           enabled: true },
      { label: 'What-if',            to: '/reporting/whatif',                enabled: true },
    ],
  },
  {
    key: 'reports',
    label: 'Reports',
    icon: FileText,
    enabled: true,
    children: [
      { label: 'All reports',          to: '/reports',                       enabled: true },
      { label: 'Daily transactions',   to: '/reports/daily-transactions',    enabled: true },
      { label: 'Delivery SLA',         to: '/reports/delivery-sla',          enabled: true },
      { label: 'Fleet operations',     to: '/reports/fleet-ops',             enabled: true },
      { label: 'KYC compliance',       to: '/reports/kyc-compliance',        enabled: true },
      { label: 'Audit & compliance',   to: '/reports/audit-compliance',      enabled: true },
      { label: 'Reconciliation',       to: '/reports/reconciliation',        enabled: true },
      { label: 'Revenue sharing',      to: '/reports/revenue-sharing',       enabled: true },
      { label: 'Staff productivity',   to: '/reports/staff-productivity',    enabled: true },
      { label: 'Gov services monthly', to: '/reports/gov-services-monthly',  enabled: true },
      { label: 'Franchise scorecard',  to: '/reports/franchise-scorecard',   enabled: true },
    ],
  },
  {
    key: 'security',
    label: 'Security',
    icon: FileText,
    enabled: true,
    children: [
      { label: 'Dashboard',         to: '/security',                  enabled: true },
      { label: 'Users',             to: '/security/users',            enabled: true },
      { label: 'My profile',        to: '/security/users/me',         enabled: true },
      { label: 'Roles',             to: '/security/roles',            enabled: true },
      { label: 'Field permissions', to: '/security/field-permissions', enabled: true },
      { label: 'Audit log',         to: '/security/audit-log',        enabled: true },
    ],
  },
  {
    key: 'settings',
    label: 'Settings',
    icon: Settings,
    enabled: true,
    children: [
      { label: 'Settings hub',           to: '/settings',                         enabled: true },
      { label: 'System parameters',      to: '/settings/system-parameters',       enabled: true },
      { label: 'Tier service matrix',    to: '/settings/tier-service-matrix',     enabled: true },
      { label: 'Pricing',                to: '/settings/pricing-config',          enabled: true },
      { label: 'Pricing zones',          to: '/settings/pricing-zones',           enabled: true },
      { label: 'Service classes',        to: '/settings/service-classes',         enabled: true },
      { label: 'Surcharges',             to: '/settings/surcharges',              enabled: true },
      { label: 'Discount rules',         to: '/settings/discount-rules',          enabled: true },
      { label: 'Fraud rules',            to: '/settings/fraud-rules',             enabled: true },
      { label: 'Transaction limits',     to: '/settings/transaction-limits',      enabled: true },
      { label: 'SLA configuration',      to: '/settings/sla-config',              enabled: true },
      { label: 'Government services',    to: '/settings/gov-service-config',      enabled: true },
      { label: 'Outlet management',      to: '/settings/outlet-management',       enabled: true },
      { label: 'Notification templates', to: '/settings/notification-templates',  enabled: true },
      { label: 'Recipient accounts',     to: '/settings/recipient-accounts',      enabled: true },
      { label: 'Revenue sharing',        to: '/settings/revenue-sharing',         enabled: true },
    ],
  },
  {
    key: 'integration',
    label: 'Integration',
    icon: Network,
    enabled: true,
    children: [
      { label: 'Dashboard',         to: '/integration',           enabled: true },
      { label: 'Health monitor',    to: '/integration/health',    enabled: true },
      { label: 'API keys',          to: '/integration/api-keys',  enabled: true },
      { label: 'Webhooks',          to: '/integration/webhooks',  enabled: true },
      { label: 'Partner onboarding', to: '/integration/partners', enabled: true },
      { label: 'NIBSS',             to: '/integration/nibss',     enabled: true },
    ],
  },
  {
    key: 'admin',
    label: 'Admin',
    icon: Workflow,
    enabled: true,
    children: [
      { label: 'Email templates',     to: '/admin/email-templates', enabled: true },
      { label: 'Notification admin',  to: '/admin/notifications',   enabled: true },
      { label: 'MVP acceptance',      to: '/admin/mvp-acceptance',  enabled: true },
      { label: 'Flowcharts',          to: '/flowcharts',            enabled: true },
    ],
  },
  {
    key: 'driver-portal',
    label: 'Driver portal',
    icon: Truck,
    to: '/driver-portal',
    enabled: true,
  },
];

function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return 'O';
  }
  if (parts.length === 1) {
    return (parts[0]?.[0] ?? 'O').toUpperCase();
  }
  const first = parts[0]?.[0] ?? '';
  const last = parts[parts.length - 1]?.[0] ?? '';
  return `${first}${last}`.toUpperCase();
}

interface SidebarProps {
  readonly collapsed: boolean;
}

function Sidebar({ collapsed }: SidebarProps): JSX.Element {
  const location = useLocation();
  const [openSection, setOpenSection] = useState<string | null>(() => {
    if (location.pathname.startsWith('/mail')) return 'mail';
    if (location.pathname.startsWith('/delivery')) return 'delivery';
    if (location.pathname.startsWith('/fleet')) return 'fleet';
    if (location.pathname.startsWith('/customer-service')) return 'customer-service';
    if (location.pathname.startsWith('/kyc')) return 'kyc';
    if (location.pathname.startsWith('/government')) return 'government';
    if (location.pathname.startsWith('/service-desk')) return 'service-desk';
    if (location.pathname.startsWith('/printing')) return 'printing';
    if (location.pathname.startsWith('/franchise')) return 'franchise';
    if (location.pathname.startsWith('/financial-services')) return 'financial-services';
    if (location.pathname.startsWith('/security')) return 'security';
    if (location.pathname.startsWith('/reporting')) return 'reporting';
    if (location.pathname.startsWith('/reports')) return 'reports';
    if (location.pathname.startsWith('/settings')) return 'settings';
    if (location.pathname.startsWith('/integration')) return 'integration';
    if (location.pathname.startsWith('/admin/customers')) return 'admin-customers';
    if (
      location.pathname.startsWith('/admin') || location.pathname.startsWith('/flowcharts')
    ) {
      return 'admin';
    }
    return null;
  });

  function toggleSection(key: string): void {
    setOpenSection((current) => (current === key ? null : key));
  }

  return (
    <aside
      className={`fixed inset-y-0 left-0 z-30 bg-white border-r border-border flex flex-col transition-all duration-200 ${
        collapsed ? 'w-16' : 'w-60'
      }`}
      aria-label="Primary navigation"
    >
      <div className="h-16 flex items-center px-4 border-b border-border gap-3">
        <div className="w-8 h-8 rounded-lg bg-nipost-green flex items-center justify-center flex-shrink-0">
          <span className="text-white font-bold text-sm">N</span>
        </div>
        {!collapsed && (
          <div>
            <p className="font-bold text-sm text-gray-900">UPMP</p>
            <p className="text-[10px] text-muted leading-none">Operator console</p>
          </div>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto p-3 space-y-1 flex flex-col">
        <div className="space-y-1 flex-1">
        {NAV_SECTIONS.map((section) => {
          const Icon = section.icon;
          // Disabled top-level entry — no children, no route.
          if (!section.enabled) {
            return (
              <span
                key={section.key}
                title={section.tooltip ?? 'Coming soon'}
                aria-disabled="true"
                className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-400 cursor-not-allowed"
              >
                <Icon className="w-4 h-4 shrink-0" />
                {!collapsed && <span className="flex-1">{section.label}</span>}
              </span>
            );
          }

          // Leaf live entry.
          if (section.to && !section.children) {
            const active =
              location.pathname === section.to ||
              (section.to !== '/' && location.pathname.startsWith(`${section.to}/`));
            return (
              <NavLink
                key={section.key}
                to={section.to}
                end={section.to === '/'}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  active
                    ? 'bg-nipost-green text-white'
                    : 'text-gray-700 hover:bg-nipost-green-light hover:text-nipost-green'
                }`}
              >
                <Icon className="w-4 h-4 shrink-0" />
                {!collapsed && <span className="flex-1">{section.label}</span>}
              </NavLink>
            );
          }

          // Parent with children (Mail).
          const isOpen = openSection === section.key;
          const childActive = section.children?.some(
            (c) => c.enabled && location.pathname.startsWith(c.to),
          );
          return (
            <div key={section.key}>
              <button
                type="button"
                onClick={() => toggleSection(section.key)}
                aria-expanded={isOpen}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  childActive
                    ? 'bg-nipost-green-light text-nipost-green'
                    : 'text-gray-700 hover:bg-nipost-green-light hover:text-nipost-green'
                }`}
              >
                <Icon className="w-4 h-4 shrink-0" />
                {!collapsed && (
                  <>
                    <span className="flex-1 text-left">{section.label}</span>
                    {isOpen ? (
                      <ChevronDown className="w-3.5 h-3.5" />
                    ) : (
                      <ChevronRight className="w-3.5 h-3.5" />
                    )}
                  </>
                )}
              </button>
              {isOpen && !collapsed && section.children ? (
                <div className="mt-1 ml-7 space-y-0.5">
                  {section.children.map((child) => {
                    if (!child.enabled) {
                      return (
                        <span
                          key={child.label}
                          title={child.tooltip}
                          aria-disabled="true"
                          className="block px-2.5 py-1.5 rounded text-xs text-gray-400 cursor-not-allowed"
                        >
                          {child.label}
                        </span>
                      );
                    }
                    const active = location.pathname.startsWith(child.to);
                    return (
                      <Link
                        key={child.label}
                        to={child.to}
                        className={`block px-2.5 py-1.5 rounded text-xs transition-colors ${
                          active
                            ? 'bg-nipost-green text-white font-semibold'
                            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                        }`}
                      >
                        {child.label}
                      </Link>
                    );
                  })}
                </div>
              ) : null}
            </div>
          );
        })}
        </div>
        <div className="pt-3 border-t border-border mt-3">
          <NavLink
            to="/sitemap"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                isActive
                  ? 'bg-nipost-green text-white'
                  : 'text-muted hover:bg-nipost-green-light hover:text-nipost-green'
              }`
            }
          >
            <Map className="w-3.5 h-3.5 shrink-0" />
            {!collapsed && <span>Sitemap</span>}
          </NavLink>
        </div>
      </nav>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// GlobalSearch — header typeahead.
//
// Wires the existing service-customer lookup + service-mail tracking-number
// resolver. Detection rules (kept in sync with the comments below):
//
//   - `^[A-Z]{2}\d{9}NG$` (e.g. `NG123456789NG`) → mail-item lookup.
//     We still issue a customer lookup using the same string so the dropdown
//     can show e.g. a CUS-* number returning a customer match alongside.
//   - `^\d{11}$` (NIN or NG mobile fragment) → customer lookup, phone-first.
//     We pass `phone` AND `customerNumber` so an NG mobile, customer number,
//     or NIN-by-coincidence all surface as candidate matches.
//   - everything else → customer lookup with `fullName`.
//
// Debounce: 250ms. Up to 5 customer rows + 1 mail-item row are rendered.
// Click navigates to the existing detail surfaces:
//   customer  → /mail/customer-profile?customerId=<id>  (live)
//   mail item → /mail/items/<id>                        (live)
// ---------------------------------------------------------------------------

const TRACKING_RE = /^[A-Z]{2}\d{9}NG$/;
const ID11_RE = /^\d{11}$/;

interface SearchHit {
  readonly key: string;
  readonly kind: 'customer' | 'mail-item';
  readonly title: string;
  readonly subtitle: string;
  readonly to: string;
}

function buildCustomerHits(rows: readonly CustomerResponse[]): readonly SearchHit[] {
  return rows.slice(0, 5).map((c) => ({
    key: `cust:${c.id}`,
    kind: 'customer' as const,
    title: c.fullName,
    subtitle: `${c.customerNumber} · ${c.primaryPhone}${c.primaryEmail ? ` · ${c.primaryEmail}` : ''}`,
    to: `/mail/customer-profile?customerId=${encodeURIComponent(c.id)}`,
  }));
}

function GlobalSearch(): JSX.Element {
  const navigate = useNavigate();
  const [raw, setRaw] = useState<string>('');
  const [debounced, setDebounced] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // 250ms debounce — matches the existing CustomerLookupAutocomplete.
  useEffect(() => {
    const trimmed = raw.trim();
    if (trimmed.length < 2) {
      setDebounced('');
      return;
    }
    const handle = window.setTimeout(() => setDebounced(trimmed), 250);
    return () => window.clearTimeout(handle);
  }, [raw]);

  // Click-outside to close.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent): void {
      const root = containerRef.current;
      if (root && e.target instanceof Node && !root.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const upper = debounced.toUpperCase();
  const isTracking = TRACKING_RE.test(upper);
  const isId11 = ID11_RE.test(debounced);

  const [hits, setHits] = useState<readonly SearchHit[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  // Issue queries when `debounced` changes. We use a manual fetch (rather
  // than useQuery) because the calls fan out across two services and the
  // dropdown UX is a single "loading | results | empty" surface — a custom
  // hook would be more code than this.
  useEffect(() => {
    if (debounced.length === 0) {
      setHits([]);
      setLoading(false);
      setErrMsg(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setErrMsg(null);
    void (async (): Promise<void> => {
      try {
        const results: SearchHit[] = [];

        // 1. Mail item — only when the input matches the tracking number
        // grammar. The lookup endpoint requires Mail/readMailItem; on 401/403
        // we silently skip the mail-item row rather than fail the whole panel.
        if (isTracking) {
          try {
            const mi = await lookupMailItemByTracking(upper);
            if (!cancelled) {
              results.push({
                key: `mi:${mi.id}`,
                kind: 'mail-item',
                title: mi.trackingNumber,
                subtitle: 'Mail item · open detail',
                to: `/mail/items/${encodeURIComponent(mi.id)}`,
              });
            }
          } catch (err) {
            // 404 = no match; 403 = no permission. Either way, we drop it
            // and let the customer search still surface.
            if (
              !(err instanceof ApiError) ||
              (err.status !== 404 && err.status !== 403 && err.status !== 401)
            ) {
              throw err;
            }
          }
        }

        // 2. Customer lookup — phone first when the input is an 11-digit
        // string (NG local mobile or NIN), customer-number when starts with
        // CUS-, otherwise full-name.
        const lookupArgs = isId11
          ? { phone: debounced, pageSize: 5 }
          : /^CUS-/i.test(debounced)
            ? { customerNumber: debounced, pageSize: 5 }
            : { fullName: debounced, pageSize: 5 };
        try {
          const customerResp = await lookupCustomers(lookupArgs);
          if (!cancelled) {
            results.push(...buildCustomerHits(customerResp.items));
          }
        } catch (err) {
          // Silently skip on 401/403/400 (empty query / no permission); the
          // dropdown will still show the mail-item match if any.
          if (
            !(err instanceof ApiError) ||
            (err.status !== 400 && err.status !== 401 && err.status !== 403)
          ) {
            throw err;
          }
        }

        if (!cancelled) {
          setHits(results);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setLoading(false);
          setErrMsg(err instanceof Error ? err.message : 'Search failed');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debounced, isId11, isTracking, upper]);

  function pick(hit: SearchHit): void {
    setOpen(false);
    setRaw('');
    setDebounced('');
    navigate(hit.to);
  }

  const showDropdown = open && (loading || hits.length > 0 || errMsg !== null || debounced.length > 0);

  return (
    <div ref={containerRef} className="hidden sm:block flex-1 max-w-md relative">
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-border rounded-lg text-sm focus-within:bg-white focus-within:border-nipost-green transition-colors">
        <Search className="w-4 h-4 flex-shrink-0 text-muted" />
        <input
          type="search"
          value={raw}
          onChange={(e) => {
            setRaw(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Search waybills, customers, pages…"
          aria-label="Global search"
          data-testid="global-search-input"
          className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted"
        />
        {raw.length === 0 ? (
          <span className="hidden sm:flex items-center gap-0.5 text-[10px] text-muted bg-white border border-border rounded px-1.5 py-0.5 flex-shrink-0">
            <span>⌘</span><span>K</span>
          </span>
        ) : null}
      </div>

      {showDropdown ? (
        <div
          role="listbox"
          aria-label="Search results"
          data-testid="global-search-results"
          className="absolute left-0 right-0 mt-1 bg-white border border-border rounded-lg shadow-lg max-h-96 overflow-y-auto z-20"
        >
          {loading ? (
            <p className="px-3 py-2 text-xs text-muted">Searching…</p>
          ) : errMsg !== null ? (
            <p className="px-3 py-2 text-xs text-red-700">{errMsg}</p>
          ) : hits.length === 0 ? (
            <p className="px-3 py-2 text-xs text-muted">
              No matches for &ldquo;{debounced}&rdquo;.
              {isTracking ? ' Tracking number not found or you lack permission.' : ''}
            </p>
          ) : (
            <ul>
              {hits.map((h) => (
                <li key={h.key}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={false}
                    onClick={() => pick(h)}
                    data-testid={`global-search-hit-${h.kind}`}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-border last:border-b-0 flex items-start gap-2"
                  >
                    <span
                      className={`mt-0.5 inline-flex flex-shrink-0 items-center justify-center w-5 h-5 rounded text-[10px] font-semibold ${
                        h.kind === 'mail-item'
                          ? 'bg-blue-50 text-blue-700'
                          : 'bg-nipost-green-light text-nipost-green'
                      }`}
                      aria-hidden="true"
                    >
                      {h.kind === 'mail-item' ? 'M' : 'C'}
                    </span>
                    <span className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{h.title}</p>
                      <p className="text-[11px] text-muted truncate">{h.subtitle}</p>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}

export function StaffLayout(): JSX.Element {
  const { token, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [userMenuOpen, setUserMenuOpen] = useState<boolean>(false);

  const claims = useMemo(() => decodeStaffClaims(token), [token]);
  const displayName = claims?.displayName?.trim().length
    ? claims.displayName
    : 'Operator';
  const shortName = displayName.split(/\s+/)[0] ?? 'Operator';
  const initials = initialsFor(displayName);
  const roleLabel = claims?.role ?? '';

  function handleSignOut(): void {
    setUserMenuOpen(false);
    logout();
    navigate('/auth/login', { replace: true });
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="hidden lg:block">
        <Sidebar collapsed={collapsed} />
      </div>

      <div className={`transition-all duration-200 ${collapsed ? 'lg:ml-16' : 'lg:ml-60'}`}>
        <header className="sticky top-0 z-10 h-16 bg-white border-b border-border flex items-center gap-3 px-4 lg:px-6">
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            className="p-2 -ml-2 rounded-lg hover:bg-gray-100 flex-shrink-0"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <Menu className="w-5 h-5 text-gray-600" />
          </button>

          <GlobalSearch />


          <div className="flex items-center gap-3 ml-auto flex-shrink-0">
            {/* Notification bell placeholder — wired alongside service-notification inbox. */}
            <button
              type="button"
              disabled
              title="Notifications inbox — Phase 2 follow-up"
              className="p-2 rounded-lg text-gray-400 cursor-not-allowed"
              aria-label="Notifications"
            >
              <Bell className="w-4 h-4" />
            </button>

            <div className="hidden sm:block relative pl-3 border-l border-border">
              <button
                type="button"
                onClick={() => setUserMenuOpen((v) => !v)}
                className="flex items-center gap-2 hover:bg-gray-50 rounded-lg px-2 py-1.5 transition-colors"
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
              >
                <div className="w-8 h-8 rounded-full bg-nipost-green flex items-center justify-center">
                  <span className="text-white text-xs font-medium">{initials}</span>
                </div>
                <div className="hidden md:block text-left">
                  <p className="text-xs font-semibold text-gray-900 leading-none">{shortName}</p>
                  {roleLabel ? (
                    <p className="text-[10px] text-muted leading-none mt-0.5">{roleLabel}</p>
                  ) : (
                    <p className="text-[10px] text-muted leading-none mt-0.5">Staff</p>
                  )}
                </div>
                <ChevronDown className="w-4 h-4 text-muted" />
              </button>
              {userMenuOpen ? (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setUserMenuOpen(false)}
                    aria-hidden="true"
                  />
                  <div
                    role="menu"
                    className="absolute right-0 mt-2 w-56 bg-white rounded-xl border border-border shadow-lg z-20 py-1"
                  >
                    <div className="px-4 py-3 border-b border-border">
                      <p className="text-xs font-semibold text-gray-900">{displayName}</p>
                      {roleLabel ? (
                        <p className="text-[10px] text-muted mt-0.5">{roleLabel}</p>
                      ) : null}
                    </div>
                    <Link
                      to="/security/users/me"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      role="menuitem"
                    >
                      <UserIcon className="w-4 h-4" />
                      My Profile
                    </Link>
                    <button
                      type="button"
                      onClick={handleSignOut}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-gray-50 w-full text-left border-t border-border mt-1"
                      role="menuitem"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign out
                    </button>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </header>

        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
